import React from 'react';
import isEmpty from 'lodash.isempty';
import { PersonalDetails } from '@trp-individual-investor-adv-svc/ii-prosp-experiences/dist/index';
import '@trp-beacon-ui-kit/foundation/style-tokens/css/variables.css';
import '@trp-beacon-ui-kit/foundation/fonts/fonts.css';

import api from '../../../actions/EndPoints';
import axiosInstance from '../../../actions/AxiosInstance';
import { closeModal, commonErrorPromise, setLastRequestTime, showModal } from '../../../Utils';
import { postInvitePartner, getRtqData } from '../../shared/ApiCalls/ApiCalls';
import { PROSP_MODAL_PERSONAL_DETAILS, PROSP_MODAL_RISK_TOLERANCE_QUESTIONNAIRE } from '../../../Constants';

const coclientHasData = (clientInfo) =>
    !isEmpty(clientInfo?.coclient) &&
    clientInfo?.coclient &&
    (clientInfo?.maritalStatus === 'MARRIED' ||
        clientInfo?.maritalStatus === 'DOMESTIC_PARTNER');

const fixPayload = (clientInfo) => {
    const getClientIncome = clientInfo?.income
        ? Number(clientInfo.income)
        : 0;

    if (!coclientHasData(clientInfo)) {
        return {
            firstName: clientInfo?.firstName?.trim(),
            gender: {
                enumerationName: clientInfo?.gender?.enumerationName,
                enumerationID: clientInfo?.gender?.enumerationID,
            },
            income: getClientIncome,
            dateOfBirth: clientInfo?.dateOfBirth,
            employmentStatus: clientInfo?.employmentStatus,
            maritalStatus: clientInfo?.maritalStatus,
            stateOfResidence: clientInfo?.stateOfResidence,
            coclient: undefined,
        };
    }
    const getCoClientIncome = clientInfo?.coclient?.coclientIncome
        ? Number(clientInfo.coclient.coclientIncome)
        : 0;

    const getClientEmpStat =
        clientInfo?.employmentStatus === 'HOMEMAKER' &&
            clientInfo?.coclient?.coclientEmploymentStatus === 'RETIRED'
            ? 'RETIRED'
            : clientInfo?.employmentStatus;
    return {
        income: getClientIncome,
        firstName: clientInfo?.firstName?.trim(),
        employmentStatus: getClientEmpStat,
        gender: {
            enumerationName: clientInfo?.gender?.enumerationName,
            enumerationID: clientInfo?.gender?.enumerationID,
        },
        dateOfBirth: clientInfo?.dateOfBirth,
        maritalStatus: clientInfo?.maritalStatus,
        stateOfResidence: clientInfo?.stateOfResidence,
        coclient: {
            coclientDateOfBirth: clientInfo?.coclient?.coclientDateOfBirth,
            coclientEmploymentStatus: clientInfo?.coclient?.coclientEmploymentStatus,
            coclientFirstName: clientInfo?.coclient?.coclientFirstName?.trim(),
            coclientIncome: Number(getCoClientIncome),
            coclientGender: {
                enumerationName: clientInfo?.coclient?.coclientGender?.enumerationName,
                enumerationID: clientInfo?.coclient?.coclientGender?.enumerationID,
            },
        },
    };
};

const PersonalDetailsModal = ({ state, dispatch, _testInvitingPartner /* test-only prop */ }) => {
    const isClientPreferences = state?.clientPreferences?.preferences && state.clientPreferences.preferences.length > 0;
    const getKycLearningStyle = isClientPreferences
        ? state?.clientPreferences?.preferences[0]?.preferenceValue
        : '';
    const [clientInfo, setClientInfo] = React.useState({
        ...(state?.kycData || {}),
        learningStyle: getKycLearningStyle,
    });
    const [initialKycData, setInitialKycData] = React.useState({ ...state.kycData });
    const [isKycPostLoading, setKycPostLoading] = React.useState(false);
    const [isClientPrefUpdated, setClientPrefUpdated] = React.useState(false);
    const [isInvitingPartner, setInvitingPartner] = React.useState(_testInvitingPartner || false);
    const [hasSentInvite, setHasSentInvite] = React.useState(state?.activeInvite || state.numberOfClients > 1);


    const openNextModal = () => {
        showModal(PROSP_MODAL_RISK_TOLERANCE_QUESTIONNAIRE);
    };

    React.useEffect(() => {
        const buildClientInfo = {
            ...state.kycData,
            ...(state?.activeInvite && { maritalStatus: 'MARRIED' }),
            learningStyle:
                state?.clientPreferences?.preferences?.length > 0
                    ? state?.clientPreferences?.preferences[0]?.preferenceValue
                    : null,
        };
        setClientInfo(buildClientInfo);
        setInitialKycData({
            ...state.kycData,
            ...(state?.activeInvite && { maritalStatus: 'MARRIED' }),
        });
    }, [state.kycData, state.clientPreferences, state.activeInvite]);

    async function postInvite() {
        if (hasSentInvite || !coclientHasData(clientInfo) || isInvitingPartner === 'no' || !isInvitingPartner) {
            return Promise.resolve();
        }

        try {
            await postInvitePartner(clientInfo?.coclient?.email, clientInfo?.coclient?.pin, state.apiKey);
            setLastRequestTime();
            setHasSentInvite(true);
        } catch (error) {
            commonErrorPromise(error)
                .then(() => setHasSentInvite(false));
            throw error;
        }
    }

    async function updateClientPreferences() {
        setKycPostLoading(true);
        setClientPrefUpdated(true);
        const headerPayload = {
            preferences: [
                {
                    preference: 'Advice Learning Preference',
                    preferenceValue: clientInfo?.learningStyle,
                },
            ],
        };

        try {
            await axiosInstance.put(api.clientPreferencesAPI, headerPayload, {
                headers: { apikey: `${state.apiKey}` },
            });
            setLastRequestTime();
        } catch (error) {
            commonErrorPromise(error)
                .then(() => setClientPrefUpdated(false))
                .then(() => closeModal(PROSP_MODAL_PERSONAL_DETAILS));
            throw error;
        }
    }

    async function postKyc() {
        setKycPostLoading(true);
        try {
            const postCall = await axiosInstance.post(api.knowYourClientAPI, fixPayload(clientInfo), {
                headers: { apikey: `${state.apiKey}` },
            });

            if (postCall) {
                setLastRequestTime();
                dispatch({
                    type: 'PROSP_COMPL_SEC',
                    lastProspSection: 'kyc',
                });
                dispatch({
                    type: 'PROSP_STATUSES',
                    prospStatuses: ['NEWP', 'KYCC'],
                });
                if (
                    coclientHasData(clientInfo)
                ) {
                    dispatch({
                        type: 'HAS_PARTNER',
                        hasPartner: true,
                    });
                } else {
                    dispatch({
                        type: 'HAS_PARTNER',
                        hasPartner: false,
                    });
                }
            }
        } catch (error) {
            commonErrorPromise(error)
                .then(() => closeModal(PROSP_MODAL_PERSONAL_DETAILS))
                .then(() => setKycPostLoading(false));
            throw error;
        }
    }

    const finalStep = () => {
        if (isEmpty(state?.kycData)) {
            postInvite()
                .then(() => updateClientPreferences())
                .then(() => postKyc())
                .then(() => getRtqData(state?.apiKey, dispatch))
                .then(() => openNextModal())
                .then(() => closeModal(PROSP_MODAL_PERSONAL_DETAILS))
                .catch((err => console.log(err)));
        } else {
            getRtqData(state?.apiKey, dispatch)
                .then(() => openNextModal())
                .then(() => closeModal(PROSP_MODAL_PERSONAL_DETAILS) )
        }
    };

    return (
        <PersonalDetails
            initialKycData={initialKycData}
            account={clientInfo}
            setAccount={setClientInfo}
            saveClientInfo={postKyc}
            isInvitingPartner={isInvitingPartner}
            setInvitingPartner={setInvitingPartner}
            hasSentInvite={hasSentInvite}
            postInvite={postInvite}
            saveClientPreferences={updateClientPreferences}
            finalStep={finalStep}
            isKycPostLoading={isKycPostLoading}
            setKycPostLoading={setKycPostLoading}
            isClientPrefUpdated={isClientPrefUpdated}
            setClientPrefUpdated={setClientPrefUpdated}
        />
    );
};

export default PersonalDetailsModal;
